import { FC } from "react";
import { StatusChip } from "../StatusChip";
import { Tag } from "../Tag";
import "./SearchResult.css";

export interface SearchResultTag {
  /**
   * Label text for the tag
   */
  label: string;

  /**
   * Visual variant of the tag
   */
  variant?: "outlined" | "solid";

  /**
   * Status color for solid variant
   */
  status?: "success" | "error";

  /**
   * Size of the tag
   */
  size?: "small" | "medium";
}

export interface SearchResultProps {
  /**
   * Primary text/title of the result
   */
  title: string;

  /**
   * Secondary text/subtitle displayed below the title
   */
  subtitle?: string;

  /**
   * Additional details shown in a third line
   */
  details?: string;

  /**
   * Status information for the chip displayed in top-right
   */
  status?: {
    label: string;
    variant: "todo" | "done" | "error" | "final";
  };

  /**
   * Array of tags to display
   */
  tags?: SearchResultTag[];

  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Click handler for the result card
   */
  onClick?: () => void;

  /**
   * Test ID for testing
   */
  "data-testid"?: string;
}

/**
 * SearchResult component
 *
 * A consistent result card layout for displaying search results with title, subtitle,
 * details, status, and tags. Commonly used within SearchCombobox dropdowns.
 *
 * @example
 * ```tsx
 * <SearchResult
 *   title="John Doe"
 *   subtitle="Appointment on Sept 21, 2023"
 *   details="Claim #: 123456"
 *   status="todo"
 *   tags={[
 *     { label: 'QME', variant: 'outlined' },
 *     { label: 'Spanish', variant: 'solid', status: 'success' }
 *   ]}
 * />
 * ```
 */
export const SearchResult: FC<SearchResultProps> = ({
  title,
  subtitle,
  details,
  status,
  tags = [],
  className = "",
  onClick,
  "data-testid": dataTestId,
}) => {
  return (
    <div
      className={`indigo-search-result ${className}`}
      onClick={onClick}
      data-testid={dataTestId}
    >
      <div className="indigo-search-result__content">
        <div className="indigo-search-result__header">
          <div className="indigo-search-result__tags">
            {status && (
              <StatusChip
                label={status.label}
                variant={status.variant}
                size="small"
              />
            )}
            {tags.map((tag, index) => (
              <Tag
                key={index}
                label={tag.label}
                variant={tag.variant}
                status={tag.status}
                size={tag.size}
              />
            ))}
          </div>
        </div>

        <div className="indigo-search-result__title">{title}</div>

        {subtitle && (
          <div className="indigo-search-result__subtitle">{subtitle}</div>
        )}

        {details && (
          <div className="indigo-search-result__details">{details}</div>
        )}
      </div>
    </div>
  );
};

export default SearchResult;
